import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const ThemeContext = createContext(null);

// Comprehensive theme system with all UI color mappings
// Ordered with recommended themes first: Slate, Zinc, Nature, Midnight, Mint
export const THEME_COLORS = {
  slate: {
    name: 'Slate',
    description: 'Professional dark blue-gray',
    primary: {
      50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1',
      400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155',
      800: '#1e293b', 900: '#0f172a',
    },
    sidebar: { bg: '#0f172a', bgHover: '#1e293b', text: '#94a3b8', textHover: '#f8fafc', border: '#1e293b' },
    accent: { bg: '#3b82f6', bgHover: '#2563eb', text: '#ffffff', light: '#dbeafe', lightText: '#1e40af' },
  },
  zinc: {
    name: 'Zinc',
    description: 'Clean neutral gray',
    primary: {
      50: '#fafafa', 100: '#f4f4f5', 200: '#e4e4e7', 300: '#d4d4d8',
      400: '#a1a1aa', 500: '#71717a', 600: '#52525b', 700: '#3f3f46',
      800: '#27272a', 900: '#18181b',
    },
    sidebar: { bg: '#18181b', bgHover: '#27272a', text: '#a1a1aa', textHover: '#fafafa', border: '#27272a' },
    accent: { bg: '#6366f1', bgHover: '#4f46e5', text: '#ffffff', light: '#e0e7ff', lightText: '#4338ca' },
  },
  nature: {
    name: 'Nature',
    description: 'Calm cream & forest green',
    primary: {
      50: '#faf8f3', 100: '#f5f0e6', 200: '#ebe5d6', 300: '#ddd4be',
      400: '#c4b896', 500: '#a89a6e', 600: '#8a7c52', 700: '#6b5f3e',
      800: '#4d442d', 900: '#2f291b',
    },
    sidebar: { bg: '#1a3a1c', bgHover: '#28682d', text: '#a8d5ab', textHover: '#ffffff', border: '#28682d' },
    accent: { bg: '#28682d', bgHover: '#1a3a1c', text: '#ffffff', light: '#d4edda', lightText: '#1a3a1c' },
    pageBg: '#F0EAD6',
  },
  mint: {
    name: 'Mint',
    description: 'Cool aqua mint',
    recommended: true,
    primary: {
      50: '#effcf9', 100: '#c7f9ed', 200: '#9af5e1', 300: '#5aecd0',
      400: '#26deba', 500: '#10c9a6', 600: '#08a388', 700: '#06836e',
      800: '#086758', 900: '#075449',
    },
    sidebar: { bg: '#075449', bgHover: '#086758', text: '#5aecd0', textHover: '#ffffff', border: '#086758' },
    accent: { bg: '#10c9a6', bgHover: '#08a388', text: '#ffffff', light: '#c7f9ed', lightText: '#075449' },
  },
  blue: {
    name: 'Blue',
    description: 'Vibrant trust blue',
    primary: {
      50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd',
      400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8',
      800: '#1e40af', 900: '#1e3a8a',
    },
    sidebar: { bg: '#1e3a8a', bgHover: '#1e40af', text: '#93c5fd', textHover: '#ffffff', border: '#1e40af' },
    accent: { bg: '#2563eb', bgHover: '#1d4ed8', text: '#ffffff', light: '#dbeafe', lightText: '#1e40af' },
  },
  violet: {
    name: 'Violet',
    description: 'Creative purple',
    primary: {
      50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd',
      400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9',
      800: '#5b21b6', 900: '#4c1d95',
    },
    sidebar: { bg: '#4c1d95', bgHover: '#5b21b6', text: '#c4b5fd', textHover: '#ffffff', border: '#5b21b6' },
    accent: { bg: '#7c3aed', bgHover: '#6d28d9', text: '#ffffff', light: '#ede9fe', lightText: '#5b21b6' },
  },
  rose: {
    name: 'Rose',
    description: 'Bold vibrant pink',
    primary: {
      50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af',
      400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c',
      800: '#9f1239', 900: '#881337',
    },
    sidebar: { bg: '#881337', bgHover: '#9f1239', text: '#fda4af', textHover: '#ffffff', border: '#9f1239' },
    accent: { bg: '#e11d48', bgHover: '#be123c', text: '#ffffff', light: '#ffe4e6', lightText: '#9f1239' },
  },
  amber: {
    name: 'Amber',
    description: 'Warm golden orange',
    primary: {
      50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d',
      400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309',
      800: '#92400e', 900: '#78350f',
    },
    sidebar: { bg: '#78350f', bgHover: '#92400e', text: '#fcd34d', textHover: '#ffffff', border: '#92400e' },
    accent: { bg: '#d97706', bgHover: '#b45309', text: '#ffffff', light: '#fef3c7', lightText: '#92400e' },
  },
  teal: {
    name: 'Teal',
    description: 'Fresh ocean green',
    primary: {
      50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4',
      400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e',
      800: '#115e59', 900: '#134e4a',
    },
    sidebar: { bg: '#134e4a', bgHover: '#115e59', text: '#5eead4', textHover: '#ffffff', border: '#115e59' },
    accent: { bg: '#0d9488', bgHover: '#0f766e', text: '#ffffff', light: '#ccfbf1', lightText: '#0f766e' },
  },
  indigo: {
    name: 'Indigo',
    description: 'Deep royal blue',
    primary: {
      50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc',
      400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca',
      800: '#3730a3', 900: '#312e81',
    },
    sidebar: { bg: '#312e81', bgHover: '#3730a3', text: '#a5b4fc', textHover: '#ffffff', border: '#3730a3' },
    accent: { bg: '#4f46e5', bgHover: '#4338ca', text: '#ffffff', light: '#e0e7ff', lightText: '#4338ca' },
  },
  emerald: {
    name: 'Emerald',
    description: 'Rich forest green',
    primary: {
      50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7',
      400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857',
      800: '#065f46', 900: '#064e3b',
    },
    sidebar: { bg: '#064e3b', bgHover: '#065f46', text: '#6ee7b7', textHover: '#ffffff', border: '#065f46' },
    accent: { bg: '#059669', bgHover: '#047857', text: '#ffffff', light: '#d1fae5', lightText: '#065f46' },
  },
  crimson: {
    name: 'Crimson',
    description: 'Deep elegant red',
    primary: {
      50: '#fef2f2', 100: '#fee2e2', 200: '#fecaca', 300: '#fca5a5',
      400: '#f87171', 500: '#ef4444', 600: '#dc2626', 700: '#b91c1c',
      800: '#991b1b', 900: '#7f1d1d',
    },
    sidebar: { bg: '#7f1d1d', bgHover: '#991b1b', text: '#fca5a5', textHover: '#ffffff', border: '#991b1b' },
    accent: { bg: '#dc2626', bgHover: '#b91c1c', text: '#ffffff', light: '#fee2e2', lightText: '#991b1b' },
  },
  ocean: {
    name: 'Ocean',
    description: 'Deep sea blue-green',
    primary: {
      50: '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd', 300: '#7dd3fc',
      400: '#38bdf8', 500: '#0ea5e9', 600: '#0284c7', 700: '#0369a1',
      800: '#075985', 900: '#0c4a6e',
    },
    sidebar: { bg: '#0c4a6e', bgHover: '#075985', text: '#7dd3fc', textHover: '#ffffff', border: '#075985' },
    accent: { bg: '#0284c7', bgHover: '#0369a1', text: '#ffffff', light: '#e0f2fe', lightText: '#075985' },
  },
  pink: {
    name: 'Baby Pink',
    description: 'Soft feminine pink',
    primary: {
      50: '#fdf2f8', 100: '#fce7f3', 200: '#fbcfe8', 300: '#f9a8d4',
      400: '#f472b6', 500: '#ec4899', 600: '#db2777', 700: '#be185d',
      800: '#9d174d', 900: '#831843',
    },
    sidebar: { bg: '#831843', bgHover: '#9d174d', text: '#f9a8d4', textHover: '#ffffff', border: '#9d174d' },
    accent: { bg: '#db2777', bgHover: '#be185d', text: '#ffffff', light: '#fce7f3', lightText: '#9d174d' },
  },
  minimal: {
    name: 'Minimal',
    description: 'Clean navy & white',
    primary: {
      50: '#ffffff', 100: '#f8fafc', 200: '#f1f5f9', 300: '#e2e8f0',
      400: '#cbd5e1', 500: '#94a3b8', 600: '#64748b', 700: '#475569',
      800: '#1e293b', 900: '#0f172a',
    },
    sidebar: { bg: '#0f172a', bgHover: '#1e293b', text: '#64748b', textHover: '#ffffff', border: '#1e293b' },
    accent: { bg: '#334155', bgHover: '#475569', text: '#ffffff', light: '#e2e8f0', lightText: '#334155' },
    pageBg: '#ffffff',
  },
};

// Apply theme CSS variables to document
const applyTheme = (themeKey) => {
  const theme = THEME_COLORS[themeKey];
  if (!theme) return;

  const root = document.documentElement;

  // Set primary color scale
  Object.entries(theme.primary).forEach(([shade, color]) => {
    root.style.setProperty(`--color-theme-${shade}`, color);
  });

  // Set sidebar colors
  root.style.setProperty('--sidebar-bg', theme.sidebar.bg);
  root.style.setProperty('--sidebar-bg-hover', theme.sidebar.bgHover);
  root.style.setProperty('--sidebar-text', theme.sidebar.text);
  root.style.setProperty('--sidebar-text-hover', theme.sidebar.textHover);
  root.style.setProperty('--sidebar-border', theme.sidebar.border);

  // Set accent colors (for buttons, badges, highlights)
  root.style.setProperty('--accent-bg', theme.accent.bg);
  root.style.setProperty('--accent-bg-hover', theme.accent.bgHover);
  root.style.setProperty('--accent-text', theme.accent.text);
  root.style.setProperty('--accent-light', theme.accent.light);
  root.style.setProperty('--accent-light-text', theme.accent.lightText);

  // Set page background (for themes with custom backgrounds like Nature)
  root.style.setProperty('--page-bg', theme.pageBg || '#f8fafc');

  // Set data attribute for Tailwind
  root.setAttribute('data-theme', themeKey);
};

// Bottom nav style options
export const BOTTOM_NAV_STYLES = {
  classic: {
    name: 'Classic',
    description: 'White bar with labels always visible'
  },
  pill: {
    name: 'Pill',
    description: 'White bar, active item shows pill with label'
  },
  floating: {
    name: 'Floating Pill',
    description: 'Dark floating bar, active shows pill with label'
  },
  floatingClassic: {
    name: 'Floating Classic',
    description: 'Dark floating bar with labels always visible'
  }
};

// UI Style options (Classic vs Modern)
export const UI_STYLES = {
  classic: {
    name: 'Classic',
    description: 'Traditional clean interface'
  },
  modern: {
    name: 'Modern',
    description: 'Enhanced animations & effects'
  }
};

// Dashboard Style options (Simple vs Box)
export const DASHBOARD_STYLES = {
  simple: {
    name: 'Simple',
    description: 'Clean traditional dashboard layout'
  },
  box: {
    name: 'Box',
    description: 'Modern banking-style with cards'
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // Get theme from localStorage or default to 'slate'
    return localStorage.getItem('theme') || 'slate';
  });
  const [bottomNavStyle, setBottomNavStyle] = useState(() => {
    // Get bottom nav style from localStorage or default to 'floatingClassic'
    return localStorage.getItem('bottomNavStyle') || 'floatingClassic';
  });
  const [uiStyle, setUiStyle] = useState(() => {
    // Get UI style from localStorage or default to 'modern'
    return localStorage.getItem('uiStyle') || 'modern';
  });
  const [dashboardStyle, setDashboardStyle] = useState(() => {
    // Get dashboard style from localStorage or default to 'box'
    return localStorage.getItem('dashboardStyle') || 'box';
  });
  const [saving, setSaving] = useState(false);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Listen for theme changes from login
  useEffect(() => {
    const handleThemeChange = (event) => {
      const newTheme = event.detail;
      if (newTheme && THEME_COLORS[newTheme]) {
        setTheme(newTheme);
      }
    };
    window.addEventListener('theme-change', handleThemeChange);
    return () => window.removeEventListener('theme-change', handleThemeChange);
  }, []);

  // Sync theme from user profile on login
  const syncThemeFromUser = useCallback((userTheme) => {
    if (userTheme && THEME_COLORS[userTheme]) {
      setTheme(userTheme);
    }
  }, []);

  const changeTheme = useCallback(async (newTheme) => {
    if (!THEME_COLORS[newTheme]) return;

    setTheme(newTheme);

    // Save to backend if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      setSaving(true);
      try {
        await authAPI.updateProfile({ theme: newTheme });
        // Update user in localStorage with new theme
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          user.theme = newTheme;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } catch (err) {
        console.error('Failed to save theme to server:', err);
      } finally {
        setSaving(false);
      }
    }
  }, []);

  const changeBottomNavStyle = useCallback((newStyle) => {
    if (!BOTTOM_NAV_STYLES[newStyle]) return;
    setBottomNavStyle(newStyle);
    localStorage.setItem('bottomNavStyle', newStyle);
  }, []);

  const changeUiStyle = useCallback((newStyle) => {
    if (!UI_STYLES[newStyle]) return;
    setUiStyle(newStyle);
    localStorage.setItem('uiStyle', newStyle);
  }, []);

  const changeDashboardStyle = useCallback((newStyle) => {
    if (!DASHBOARD_STYLES[newStyle]) return;
    setDashboardStyle(newStyle);
    localStorage.setItem('dashboardStyle', newStyle);
  }, []);

  const value = {
    theme,
    changeTheme,
    syncThemeFromUser,
    saving,
    themeColors: THEME_COLORS[theme],
    availableThemes: THEME_COLORS,
    bottomNavStyle,
    changeBottomNavStyle,
    availableNavStyles: BOTTOM_NAV_STYLES,
    uiStyle,
    changeUiStyle,
    availableUiStyles: UI_STYLES,
    isModernUI: uiStyle === 'modern',
    dashboardStyle,
    changeDashboardStyle,
    availableDashboardStyles: DASHBOARD_STYLES
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
